import React, { useState, useEffect, useRef } from 'react';
import { auth, provider, signInWithPopup, signOut } from '../../firebase';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import { Link, useLocation } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { GiGalaxy } from "react-icons/gi";
import Loader from './loader';
import { faPencil, faHomeLg, faPeopleGroup, faImage, faBlog, faBook, faBars, faXmark, faPhone } from '@fortawesome/free-solid-svg-icons';
import { useTranslation } from 'react-i18next';
import Style from '../assets/css/navbar.module.css';

const Navbar = () => {
    const [user, setUser] = useState(null);
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const [windowWidth, setWindowWidth] = useState(window.innerWidth);
    const location = useLocation();
    const menuRef = useRef(null);
    const auth = getAuth();
    const [showLoader, setShowLoader] = useState(false);
    const { t } = useTranslation();

    useEffect(() => {
        // Muestra el loader durante 0.3s cada vez que cambia la ruta
        setShowLoader(true);
        const timer = setTimeout(() => {
            setShowLoader(false);
        }, 300);

        return () => clearTimeout(timer);
    }, [location]);

    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
            setUser(currentUser);
        });
        return () => unsubscribe();
    }, [auth]);

    const handleLogin = async () => {
        try {
            const result = await signInWithPopup(auth, provider);
            setUser(result.user);
        } catch (error) {
            console.error('Error logging in with Google:', error);
        }
    };

    const handleLogout = async () => {
        try {
            await signOut(auth);
            setUser(null);
            setIsMenuOpen(false);
        } catch (error) {
            console.error('Error logging out:', error);
        }
    };

    useEffect(() => {
        const unsubscribe = auth.onAuthStateChanged((user) => {
            if (user) {
                setUser(user);
            } else {
                setUser(null);
            }
        });
        return () => unsubscribe();
    }, []);

    const toggleMenu = () => {
        setIsMenuOpen((prevState) => !prevState);
    };

    useEffect(() => {
        const handleResize = () => {
            setWindowWidth(window.innerWidth);
        };

        window.addEventListener('resize', handleResize);
        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, []);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (menuRef.current && !menuRef.current.contains(event.target) && !event.target.closest(`.${Style.menuButton}`)) {
                setIsMenuOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    return (
        <>{showLoader && <Loader />}
            <nav className={Style.navbar}>
                <Link to="/" className={Style.logoLink}>
                    {windowWidth <= 430 ? (
                        <img src="https://firebasestorage.googleapis.com/v0/b/nasa-bae28.appspot.com/o/AndiviNundoo.webp?alt=media&token=37d03aed-1a71-4f28-a90d-cfdb1be679c4" alt="Andivi Nundoo" className={Style.logoImage} />
                    ) : (
                        <h1 className={Style.logo}>Andivi Nundoo</h1>
                    )}
                </Link>
                {windowWidth <= 430 ? (
                    <button className={Style.menuButton} aria-label='Bars' onClick={toggleMenu}>
                        <FontAwesomeIcon icon={isMenuOpen ? faXmark : faBars} />
                    </button>
                ) : (
                    <div className={Style.navLinks}>
                        <Link to="/" aria-label="Home" className={`${Style.navLinkHome} ${location.pathname === '/' ? Style.activeLine : ''}`} >
                            <FontAwesomeIcon className={Style.homeSvgIcon} icon={faHomeLg} />
                        </Link>
                        <Link to="/draw2D" aria-label="Pencil" className={`${Style.navLinkDraw} ${location.pathname === '/draw2D' || location.pathname === '/draw3D' ? Style.activeLine : ''}`}>
                            <FontAwesomeIcon className={Style.editSvgIcon} icon={faPencil} />
                        </Link>
                        <Link to="/drawings" aria-label="Book" className={`${Style.navLinkDrawings} ${location.pathname === '/drawings' ? Style.activeLine : ''}`}>
                            <FontAwesomeIcon className={Style.drawingsSvgIcon} icon={faBook} />
                        </Link>
                        <Link to="/stellarium" aria-label="Earth" className={`${Style.navLinkEarth} ${location.pathname === '/stellarium' ? Style.activeLine : ''}`}>
                            <GiGalaxy className={Style.earthSvgIcon} />
                        </Link>
                        {/* <Link to="/picture" aria-label="Image" className={`${Style.navLinkPicture} ${location.pathname === '/picture' ? Style.activeLine : ''}`}>
                            <FontAwesomeIcon className={Style.pictureSvgIcon} icon={faImage} />
                        </Link> */}
                        <Link to="/blog" aria-label="Blog" className={`${Style.navLinkBlog} ${location.pathname === '/blog' ? Style.activeLine : ''}`}>
                            <FontAwesomeIcon className={Style.blogSvgIcon} icon={faBlog} />
                        </Link>
                        <Link to="/us" aria-label="Team" className={`${Style.navLinkTeam} ${location.pathname === '/us' ? Style.activeLine : ''}`}>
                            <FontAwesomeIcon className={Style.teamSvgIcon} icon={faPeopleGroup} />
                        </Link>
                        <Link to="/contact" aria-label="Contact" className={`${Style.navLinkContact} ${location.pathname === '/contact' ? Style.activeLine : ''}`}>
                            <FontAwesomeIcon className={Style.contactSvgIcon} icon={faPhone} />
                        </Link>
                    </div>
                )}
                {isMenuOpen && windowWidth <= 430 && (
                    <div ref={menuRef} className={Style.mobileMenu}>
                        <Link to="/" className={`${Style.navLinkHome} ${location.pathname === '/' ? Style.activeLine : ''}`} onClick={toggleMenu}>
                            <FontAwesomeIcon className={Style.homeSvgIcon} icon={faHomeLg} />
                        </Link>
                        <Link to="/draw2D" className={`${Style.navLinkDraw} ${location.pathname === '/draw2D' || location.pathname === '/draw3D' ? Style.activeLine : ''}`} onClick={toggleMenu}>
                            <FontAwesomeIcon className={Style.editSvgIcon} icon={faPencil} />
                        </Link>
                        <Link to="/drawings" className={`${Style.navLinkDrawings} ${location.pathname === '/drawings' ? Style.activeLine : ''}`} onClick={toggleMenu}>
                            <FontAwesomeIcon className={Style.drawingsSvgIcon} icon={faBook} />
                        </Link>
                        <Link to="/stellarium" className={`${Style.navLinkEarth} ${location.pathname === '/stellarium' ? Style.activeLine : ''}`} onClick={toggleMenu}>
                            <GiGalaxy className={Style.earthSvgIcon} />
                        </Link>
                        {/* <Link to="/picture" className={`${Style.navLinkPicture} ${location.pathname === '/picture' ? Style.activeLine : ''}`} onClick={toggleMenu}>
                            <FontAwesomeIcon className={Style.pictureSvgIcon} icon={faImage} />
                        </Link> */}
                        <Link to="/blog" className={`${Style.navLinkBlog} ${location.pathname === '/blog' ? Style.activeLine : ''}`} onClick={toggleMenu}>
                            <FontAwesomeIcon className={Style.blogSvgIcon} icon={faBlog} />
                        </Link>
                        <Link to="/us" className={`${Style.navLinkTeam} ${location.pathname === '/us' ? Style.activeLine : ''}`} onClick={toggleMenu}>
                            <FontAwesomeIcon className={Style.teamSvgIcon} icon={faPeopleGroup} />
                        </Link>
                        <Link to="/contact" className={`${Style.navLinkContact} ${location.pathname === '/contact' ? Style.activeLine : ''}`}>
                            <FontAwesomeIcon className={Style.contactSvgIcon} icon={faPhone} />
                        </Link>
                        <div className={Style.dropdownWrapperMobile}>
                            {user ? (
                                <div onClick={toggleMenu} className={Style.profileWrapper}>
                                    <img src={user.photoURL} alt="Profile" className={Style.profileImage} />
                                </div>
                            ) : (
                                <button onClick={handleLogin} className={Style.loginButton}>
                                    {t('login')} <svg
                                        viewBox="0 0 256 262"
                                        preserveAspectRatio="xMidYMid"
                                        xmlns="http://www.w3.org/2000/svg"
                                        className={Style.googleIcon}
                                        width="16"
                                        height="16"
                                    >
                                        <path
                                            d="M255.878 133.451c0-10.734-.871-18.567-2.756-26.69H130.55v48.448h71.947c-1.45 12.04-9.283 30.172-26.69 42.356l-.244 1.622 38.755 30.023 2.685.268c24.659-22.774 38.875-56.282 38.875-96.027"
                                            fill="#4285F4"
                                        ></path>
                                        <path
                                            d="M130.55 261.1c35.248 0 64.839-11.605 86.453-31.622l-41.196-31.913c-11.024 7.688-25.82 13.055-45.257 13.055-34.523 0-63.824-22.773-74.269-54.25l-1.531.13-40.298 31.187-.527 1.465C35.393 231.798 79.49 261.1 130.55 261.1"
                                            fill="#34A853"
                                        ></path>
                                        <path
                                            d="M56.281 156.37c-2.756-8.123-4.351-16.827-4.351-25.82 0-8.994 1.595-17.697 4.206-25.82l-.073-1.73L15.26 71.312l-1.335.635C5.077 89.644 0 109.517 0 130.55s5.077 40.905 13.925 58.602l42.356-32.782"
                                            fill="#FBBC05"
                                        ></path>
                                        <path
                                            d="M130.55 50.479c24.514 0 41.05 10.589 50.479 19.438l36.844-35.974C195.245 12.91 165.798 0 130.55 0 79.49 0 35.393 29.301 13.925 71.947l42.211 32.783c10.59-31.477 39.891-54.251 74.414-54.251"
                                            fill="#EB4335"
                                        ></path>
                                    </svg>{t('google')}
                                </button>
                            )}
                            {user && (
                                <>
                                    <Link to="/my-drawings" className={`${Style.dropdownLink} ${location.pathname === '/my-drawings' ? Style.active : ''}`} onClick={() => {setIsMenuOpen(false);}}>
                                        {t('myDrawings')}
                                    </Link>
                                    <p className={Style.logoutMobile} onClick={handleLogout}><span>{t('logout')}</span></p>
                                </>
                            )}
                        </div>
                    </div>
                )}
                {windowWidth > 430 && (
                    <div className={Style.dropdownWrapper}>
                        {user ? (
                            <div onClick={toggleMenu} className={Style.profileWrapper}>
                                <img src={user.photoURL} alt="Profile" className={Style.profileImage} />
                            </div>
                        ) : (
                            <button onClick={handleLogin} className={Style.loginButton}>
                                {t('login')} <svg
                                    viewBox="0 0 256 262"
                                    preserveAspectRatio="xMidYMid"
                                    xmlns="http://www.w3.org/2000/svg"
                                    className={Style.googleIcon}
                                    width="16"
                                    height="16"
                                >
                                    <path
                                        d="M255.878 133.451c0-10.734-.871-18.567-2.756-26.69H130.55v48.448h71.947c-1.45 12.04-9.283 30.172-26.69 42.356l-.244 1.622 38.755 30.023 2.685.268c24.659-22.774 38.875-56.282 38.875-96.027"
                                        fill="#4285F4"
                                    ></path>
                                    <path
                                        d="M130.55 261.1c35.248 0 64.839-11.605 86.453-31.622l-41.196-31.913c-11.024 7.688-25.82 13.055-45.257 13.055-34.523 0-63.824-22.773-74.269-54.25l-1.531.13-40.298 31.187-.527 1.465C35.393 231.798 79.49 261.1 130.55 261.1"
                                        fill="#34A853"
                                    ></path>
                                    <path
                                        d="M56.281 156.37c-2.756-8.123-4.351-16.827-4.351-25.82 0-8.994 1.595-17.697 4.206-25.82l-.073-1.73L15.26 71.312l-1.335.635C5.077 89.644 0 109.517 0 130.55s5.077 40.905 13.925 58.602l42.356-32.782"
                                        fill="#FBBC05"
                                    ></path>
                                    <path
                                        d="M130.55 50.479c24.514 0 41.05 10.589 50.479 19.438l36.844-35.974C195.245 12.91 165.798 0 130.55 0 79.49 0 35.393 29.301 13.925 71.947l42.211 32.783c10.59-31.477 39.891-54.251 74.414-54.251"
                                        fill="#EB4335"
                                    ></path>
                                </svg>
                                {t('google')}
                            </button>
                        )}
                        {isMenuOpen && user && (
                            <div ref={menuRef} className={Style.dropdownMenu}>
                                <div className={Style.upperTriangle}></div>
                                <ul className={Style.dropdownList}>
                                    <li>
                                        <Link to="/my-drawings" className={`${Style.dropdownLink} ${location.pathname === '/my-drawings' ? Style.active : ''}`} onClick={() => {setIsMenuOpen(false);}}>
                                            {t('myDrawings')}
                                        </Link>
                                    </li>
                                    <li className={Style.logOut} onClick={handleLogout}><span>{t('logout')}</span></li>
                                </ul>
                            </div>
                        )}
                    </div>
                )}
            </nav>
        </>
    );
};

export default Navbar;
